import {  Stack, SxProps, Typography, useTheme } from "@mui/material";
import { ReactNode, useLayoutEffect, useMemo, useRef, useState } from "react";

interface SwitchOption {
  value: string;
  label?: string;
  width?: number;
  icon?: ReactNode;
  iconOnly?: boolean;
}

export const MultiTabSwitch =
  // memo(
  ({
    options,
    selectedValue,
    onChange,
    disabled = false,
    containerStyle = {},
    selectedItemStyle = {},
    itemStyle = {},
  }: {
    options: SwitchOption[];
    selectedValue: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    containerStyle?: SxProps;
    selectedItemStyle?: SxProps;
    itemStyle?: SxProps;
  }) => {
    const theme = useTheme();
    const refs = useRef<Array<HTMLDivElement | HTMLSpanElement>>([]);

    const [widths, setWidths] = useState<Array<number | undefined>>([]);

    const getSelectedPosition = () => {
      const selectedIndex = options.findIndex(
        (option) => option.value === selectedValue,
      );
      let position = 0;
      for (let i = 0; i < selectedIndex; i++) {
        position += widths[i] || options[i].width || 0;
      }
      const value = position + selectedIndex + 1;

      return value;
    };

    const selectedItem = useMemo(() => {
      return options.find((opt) => opt.value === selectedValue);
    }, [selectedValue]);

    useLayoutEffect(() => {
      const list = refs.current.map((ref) => ref?.offsetWidth);
      setWidths(list);
    }, [selectedValue]);

    return (
      <Stack
        direction={"row"}
        sx={{
          display: "flex",
          position: "relative",
          p: "2px",
          height: "32px",
          borderRadius: "4px",
          gap: "2px",
          alignItems: "center",
          bgcolor: theme.palette.neutral["005"],
          "&:hover": {
            cursor: disabled ? "not-allowed" : "pointer",
          },
          overflow: "hidden",
          ...containerStyle,
        }}
      >
        {options.map((option, index) => (
          <Stack
            ref={(el) => {
              if (el) {
                refs.current[index] = el;
              }
            }}
            key={option.value}
          >
            <Stack
              key={option.value}
              direction={"row"}
              alignItems={"center"}
              justifyContent={"center"}
              gap={"4px"}
              sx={{
                visibility:
                  selectedValue === option.value ? "hidden" : "visible",
                color: theme.palette.lightBg.low,
                textAlign: "center",
                p: option.iconOnly ? "2px 4px" : "2px 8px",
                whiteSpace: "nowrap",
                ...itemStyle,
              }}
              onClick={(e) => {
                if (disabled) return;
                e.stopPropagation();
                // Select the clicked option directly. Clicking the already
                // selected option is a no-op (no toggling to the other tab).
                if (option.value === selectedValue) return;
                onChange(option.value);
              }}
            >
              {!option.iconOnly && (
                <Typography
                  key={option.value}
                  variant="body-small"
                  ref={(el) => {
                    if (el) {
                      refs.current[index] = el;
                    }
                  }}
                 
                >
                  {option.label}
                </Typography>
              )}
              {option.icon && option.icon}
            </Stack>
          </Stack>
        ))}
        <Stack
          sx={{
            flexDirection: "row",
            alignItems: "center",
            gap: "4px",
            height: "28px",
            p: selectedItem?.iconOnly ? "2px 4px" : "2px 8px",
            position: "absolute",
            bgcolor: theme.palette.darkBg.high,
            boxShadow: "2px 2px 6px 0px #0000001F",
            textAlign: "center",
            borderRadius: "4px",
            whiteSpace: "nowrap",
            width: "auto",
            transform: `translateX(${getSelectedPosition()}px)`,
            transition: "transform 0.2s ease, width 0.2s ease",
            ...selectedItemStyle,
          }}
        >
          {!selectedItem?.iconOnly && (
            <Typography variant="body-small">{selectedItem?.label}</Typography>
          )}
          {selectedItem?.icon}
        </Stack>
      </Stack>
    );
  };
// )
